import { createFetchClient, FetchClient, FetchRequestConfig, ApiError } from './fetch-client';

class ApiClient {
  private client: FetchClient;

  constructor() {
    this.client = createFetchClient({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3003/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Auth token is handled automatically by the client
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        const apiError = error as ApiError;
        if (apiError.statusCode === 401) {
          // Handle unauthorized access
          this.client.setAuthToken(null);

          // Check if current request URL contains validate-token
          const currentUrl = window.location.href;
          const isValidateTokenRequest = currentUrl.includes('/auth/validate-token');

          if (isValidateTokenRequest) {
            // Just return the error for validate-token
            return Promise.reject(error);
          }

          // Redirect to login if needed (with locale)
          const pathParts = window.location.pathname.split('/');
          const locale = pathParts[1] || 'en';
          const loginPath = `/${locale}/auth/login`;
          if (!window.location.pathname.includes('/auth/login')) {
            window.location.href = loginPath;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string | null) {
    this.client.setAuthToken(token);
  }

  async get<T = unknown>(url: string, config?: FetchRequestConfig): Promise<T> {
    return this.client.get<T>(url, config);
  }

  async post<T = unknown>(url: string, data?: unknown, config?: FetchRequestConfig): Promise<T> {
    return this.client.post<T>(url, data, config);
  }

  async put<T = unknown>(url: string, data?: unknown, config?: FetchRequestConfig): Promise<T> {
    return this.client.put<T>(url, data, config);
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: FetchRequestConfig): Promise<T> {
    return this.client.patch<T>(url, data, config);
  }

  async delete<T = unknown>(url: string, config?: FetchRequestConfig): Promise<T> {
    return this.client.delete<T>(url, config);
  }

  async upload<T = unknown>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<T> {
    return this.client.upload<T>(url, formData, onProgress);
  }
}

export const apiClient = new ApiClient();